import pathsConfig from '~/config/paths.config';

export function createPath(path: string, account: string) {
  return path.replace('[account]', account);
}

export function createPathWithPage(path: string, account: string, page: string) {
  return path.replace('[account]', account).replace('[page]', page);
}

export function removeUrlPrefix(url: string): string {
  return url.replace(/^(https?:\/\/)?(www\.)?/, '');
}

export function getAccountDynamicPath(accountPath: string, fallbackPath: string, accountSlug?: string): string {
  if (accountSlug) {
    return accountPath.replace('[account]', accountSlug);
  } else {
    return fallbackPath;
  }
}

export function getProductEditorPath(accountSlug?: string, contentId?: string): string {
  if (accountSlug && contentId) {
    return pathsConfig.app.accountProductEditor.replace('[account]', accountSlug).replace('[content_id]', contentId);
  } else {
    return pathsConfig.app.home;
  }
}

export function getLinkEditorPath(accountSlug?: string, contentId?: string): string {
  if (accountSlug && contentId) {
    return pathsConfig.app.accountLinkEditor.replace('[account]', accountSlug).replace('[content_id]', contentId);
  } else {
    return pathsConfig.app.home;
  }
}

export function getAccountDynamicPathWithPage(accountPath: string, fallbackPath: string, accountSlug?: string, page?: string): string {
  if (accountSlug && page) {
    return accountPath.replace('[account]', accountSlug).replace('[page]', page);;
  } else {
    return fallbackPath;
  }
}

export function isExpired(expiredAt: Date): boolean {
  const now = new Date();
  const isExpired = expiredAt < now;
  return isExpired;
}

export function computingPowerExchangeRate(currency: 'IDR' | 'USD', value: number): number {
  if (currency === 'IDR') {
    return value;
  } else {
    return value * 15000;
  }
}

export function getDateString(date: Date) {
  const day = date.toLocaleDateString();
  const time = date.toLocaleTimeString();
  return `${day} at ${time}`;
}

export function getSimplifiedDateString(date: Date) {
  const day = date.toLocaleDateString();
  const time = date.toLocaleTimeString();

  const isToday = new Date().toLocaleDateString() === day;
  if (isToday) {
    return time;
  } else {
    return day;
  }
}

export function calculateNewExpirationDate(existingExpirationDate: Date, interval: number): Date {
  const todayDate = new Date();
  const expirationDate = existingExpirationDate > todayDate ? existingExpirationDate : todayDate;
  expirationDate.setDate(expirationDate.getDate() + (30 * interval));
  return expirationDate;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

export function getFirstItemInArray<T>(array?: T[] | undefined | null): T | undefined {
  if (!array) return undefined;
  return array.length > 0 ? array[0] : undefined;
}

/**
 * Calculates the size of a string in kilobytes (KB)
 * @param text The string to measure
 * @returns The size in KB (rounded to 2 decimal places)
 */
export function getStringSizeInKB(text: string): number {
  // TextEncoder provides accurate byte count for UTF-8 encoded strings
  const bytes = new TextEncoder().encode(text).length;
  const kilobytes = bytes / 1024;
  return Math.round(kilobytes * 100) / 100; // Round to 2 decimal places
}

export async function verifyIsPDF(file: File): Promise<boolean> {
  // First check MIME type (quick but not foolproof)
  const hasPdfMimeType = file.type === 'application/pdf';
  
  // Then verify file signature (magic numbers)
  try {
    // Read the first few bytes of the file
    const fileHeader = await readFileHeader(file, 5);
    // Check if it starts with the PDF signature "%PDF-"
    const hasPdfSignature = new TextDecoder().decode(fileHeader) === '%PDF-';
    
    return hasPdfMimeType && hasPdfSignature;
  } catch (error) {
    console.error('Error validating PDF file:', error);
    return false;
  }
}

// Helper function to read the first n bytes of a file
function readFileHeader(file: File, bytes: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result).slice(0, bytes));
      } else {
        reject(new Error('Failed to read file header'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, bytes));
  });
}

/**
 * Extracts text content from a PDF file on the client side
 * @param file PDF file to extract text from
 * @returns Promise resolving to the extracted text content
 */
export async function pdfToTextContent(file: File): Promise<string> {
  const isPdfFile = await verifyIsPDF(file);

  if (!isPdfFile) {
    throw new Error('The file is not a valid PDF.');
  }

  try {
      // Dynamic import to reduce bundle size
    const pdfjs = await import('pdfjs-dist');
    
    // Set worker path (required for PDF.js)
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url
    ).toString()

    // Convert file to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load the PDF document
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
    const pdfDocument = await loadingTask.promise;
    
    // Get total number of pages
    const numPages = pdfDocument.numPages;
    let textContent = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdfDocument.getPage(pageNum);
      const content = await page.getTextContent();
      // Concatenate the text items
      const pageText = content.items
        .map((item) => 'str' in item ? item.str : '')
        .join(' ');
      
      textContent += pageText + '\n';
    }
    
    return textContent.trim();
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    throw new Error('Failed to extract text from PDF');
  }
}

/**
 * Checks if a filename has a valid file extension
 * @param fileName The filename to check
 * @returns True if the filename has a valid extension, false otherwise
 */
export function isHasExtension(fileName?: string): boolean {
  if (!fileName) return false;
  
  // Get the last segment after splitting on dots
  const parts = fileName.split('.');
  
  // A valid extension means:
  // 1. There must be at least two parts (filename and extension)
  // 2. The last part (extension) must not be empty
  return parts.length > 1 && (parts[parts.length - 1]?.length ?? 0) > 0;
}

export function isNeedToShowDetailProduct(titles: string[], urls: string[]): boolean {
  if (titles.length === 0 || urls.length === 0) {
    return false;
  }
  const firstTitle = titles[0];
  const firstUrl = urls[0];
  return firstTitle !== firstUrl;
}

export function escapeCsvField(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function downloadCsvFile(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}